import React from 'react';
import { useTranslation } from 'react-i18next';
import Constants from '../../../assests/Constants/constants';

/**
 * Currency selector shared by the HTML ledger reports (Trial Balance, P&L, Account Card).
 *
 * Selection model (kept in the parent as `selectedCurrency`):
 *   - null      -> Shekel only (default, current behaviour, no FC columns)
 *   - 'MIXED'   -> show every foreign currency alongside Shekel
 *   - <number>  -> a single Enums.ForeignCurrencyCode value; filter to that currency
 *
 * The selector is hidden when there is no foreign-currency activity to show
 * (no available currencies and Shekel is selected).
 */
const ReportCurrencySelector = ({ selectedCurrency = null, availableCurrencies = [], onCurrencyChange }) => {
    const { t } = useTranslation();

    const codeToName = React.useMemo(() => {
        const map = {};
        Object.entries(Constants.ForeignCurrencyCode || {}).forEach(([name, value]) => {
            map[value] = name;
        });
        return map;
    }, []);

    const currencies = Array.isArray(availableCurrencies) ? availableCurrencies : [];
    const hasCurrencies = currencies.length > 0;

    // Nothing to offer: no FC anywhere and the user is on the default Shekel view.
    if (!hasCurrencies && (selectedCurrency === null || selectedCurrency === undefined)) {
        return null;
    }

    const currencyLabel = (code) => {
        const name = codeToName[code];
        if (!name) return String(code);
        const symbol = Constants.ForeignCurrencySymbol?.[name];
        return symbol ? `${name} (${symbol})` : name;
    };

    const currentValue =
        selectedCurrency === null || selectedCurrency === undefined
            ? 'ILS'
            : selectedCurrency === 'MIXED'
                ? 'MIXED'
                : String(selectedCurrency);

    const handleChange = (event) => {
        const raw = event.target.value;
        if (!onCurrencyChange) return;
        if (raw === 'ILS') {
            onCurrencyChange(null);
        } else if (raw === 'MIXED') {
            onCurrencyChange('MIXED');
        } else {
            onCurrencyChange(Number(raw));
        }
    };

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                backgroundColor: '#f8f9ff',
                borderRadius: '8px',
                border: '1px solid #e3e8ff',
                height: '36px',
            }}
        >
            <span style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>
                {t('Currency')}
            </span>
            <select
                value={currentValue}
                onChange={handleChange}
                style={{
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#374151',
                    border: '1px solid #cbd5ff',
                    borderRadius: '6px',
                    padding: '3px 6px',
                    backgroundColor: '#ffffff',
                    cursor: 'pointer',
                    outline: 'none',
                }}
            >
                <option value="ILS">{t('Shekel')}</option>
                {hasCurrencies && <option value="MIXED">{t('MixedCurrencies')}</option>}
                {currencies.map((code) => (
                    <option key={code} value={String(code)}>
                        {currencyLabel(code)}
                    </option>
                ))}
            </select>
        </div>
    );
};

export default ReportCurrencySelector;
